import axios from "axios";
import Cookies from "universal-cookie";
import snakecaseKeys from "snakecase-keys";
import camelcaseKeys from "camelcase-keys";
import APPLICATION_SETTINGS from "./settings.js";

const responseBody = (response, camelCase = true) =>
  camelCase ? camelcaseKeys(response, { deep: true }) : response;
const responseError = (error) => {
  console.error(error);
  throw error;
};

axios.defaults.baseURL = APPLICATION_SETTINGS.DJANGO_API_URL;

axios.interceptors.request.use(
  (config) => {
    if (config.authentication) {
      const cookies = new Cookies();
      const token = cookies.get("token");
      if (!config.headers) config.headers = {};
      // Use standard Authorization header expected by backend (DRF token auth)
      if (token) config.headers.Authorization = `Token ${token}`;
    }
    return config;
  },
  (error) => {
    console.log("response rejected", error);
    return Promise.reject(error);
  }
);

axios.interceptors.response.use(undefined, (error) => {
  const cookies = new Cookies();
  const status = error.response ? error.response.status : null;
  const data = error.response ? error.response.data : null;
  console.log("status", status, data);

  // Check for token expiration - handle both old and new error formats
  const isTokenExpired =
    (status === 403 && data && data["detail"] === "Invalid token.") ||
    (status === 401 && data && data["code"] === "token_not_valid");

  if (isTokenExpired) {
    console.log("cookie removed due to invalid token");
    cookies.remove("token", { path: "/" });

    // Clear any existing user data from localStorage/sessionStorage if needed
    localStorage.removeItem("userData");
    sessionStorage.clear();

    // Dispatch a custom event to notify components about token expiration
    window.dispatchEvent(new CustomEvent("tokenExpired"));
  }
  throw error;
});

const requests = {
  get: (url, camelCase = true, authentication = true) =>
    axios
      .get(url, { authentication })
      .then((response) => responseBody(response, camelCase))
      .catch(responseError),

  post: (
    url,
    body = {},
    headers = {},
    snakeCase = false,
    camelCase = false,
    authentication = true
  ) => {
    const isFormData = body instanceof FormData;

    return axios
      .post(
        url,
        snakeCase && !isFormData ? snakecaseKeys(body, { deep: true }) : body,
        {
          headers: headers,
          authentication: authentication,
        }
      )
      .then((response) => responseBody(response, camelCase))
      .catch(responseError);
  },
  put: (url, body, authentication = true) =>
    axios
      .put(url, snakecaseKeys(body, { deep: true }), { authentication })
      .then(responseBody)
      .catch(responseError),
  delete: (url, camelCase = true, authentication = true) =>
    axios
      .delete(url, { authentication })
      .then((response) => responseBody(response, camelCase))
      .catch(responseError),
  patch: (url, body) =>
    axios
      .patch(url, snakecaseKeys(body, { deep: true }))
      .then(responseBody)
      .catch(responseError),
};

export const PaymentMethodsApi = {
  list: () => requests.get("/payment-methods/", true, true),
  delete: (id) => requests.delete(`/payment-methods/${id}/`, true, true),
  setDefault: (id) =>
    requests.post(
      `/payment-methods/${id}/set-default/`,
      {},
      {},
      false,
      true,
      true
    ),
};

const JobsApi = {
  getPendingApprovals: (page = 1, limit = 10) => {
    let url = `/maintenance-requests/pending-approvals/?page=${page}&limit=${limit}`;

    return requests.get(url);
  },
  getActiveJobs: (page = 1, limit = 10, search = "") => {
    let url = `/maintenance-requests/active-jobs/?page=${page}&limit=${limit}`;
    if (search && search.trim()) {
      url += `&search=${encodeURIComponent(search.trim())}`;
    }
    return requests.get(url);
  },
  getPastJobs: (page = 1, limit = 10) => {
    let url = `/maintenance-requests/past-jobs/?page=${page}&limit=${limit}`;
    return requests.get(url);
  },
  getAvailableJobs: (page = 1, limit = 10) => {
    let url = `/maintenance-requests/available-jobs/?page=${page}&limit=${limit}`;
    return requests.get(url);
  },
  // Fetch a single maintenance request by id (for full details like full property address)
  getMaintenanceRequest: (id) => {
    const url = `/maintenance-requests/${id}/`;
    return requests.get(url, true);
  },
  approveMaintenanceRequest: (id) => {
    const url = `/maintenance-requests/${id}/approve/`;
    return requests.post(url, {});
  },
  confirmAndScheduleJob: (payload) => {
    const url = "/proposals/";
    return requests.post(url, payload);
  },
  completeJob: (body, token) => {
    const url = `/maintenance-requests/${body.id}/complete/`;
    const headers = {
      Authorization: `Token ${token}`,
    };
    return requests.post(url, body, headers);
  },
  cancelMaintenanceRequest: (id, reason) => {
    const url = `/maintenance-requests/${id}/cancel/`;
    return requests.post(url, { reason });
  },
  // Propose a change to a maintenance request (modify job)
  // POST /maintenance-requests/{id}/propose-change/
  // Supports JSON (no file) and multipart/form-data (when media file is present)
  proposeChange: (id, values = {}) => {
    const url = `/maintenance-requests/${id}/propose-change/`;
    const { additionalWork, newEstimate, media } = values || {};

    if (media) {
      const form = new FormData();
      if (additionalWork != null) form.append("additional_work", additionalWork);
      if (newEstimate != null) form.append("new_estimate", newEstimate);
      form.append("media", media);
      return requests.post(url, form);
    }

    // Send JSON with snake_cased keys expected by backend
    const body = {
      additionalWork,
      newEstimate,
    };
    return requests.post(url, body, {}, true); // snakeCase = true
  },
  // Propose a reschedule request
  // POST /maintenance-requests/{id}/propose-reschedule/
  // Body (JSON or multipart): { newDate?, newTime?, reason?, media? }
  proposeReschedule: (id, values = {}) => {
    const url = `/maintenance-requests/${id}/propose-reschedule/`;
    const {
      // support either `rescheduleReason` (from UI) or `reason`
      rescheduleReason,
      reason,
      newDate,
      newTime,
      media,
      // optional array of time slot strings like: ["2025-01-10|morning", ...]
      daysAvailable,
    } = values || {};

    const finalReason = rescheduleReason ?? reason ?? "";

    if (media) {
      const form = new FormData();
      if (finalReason !== undefined) form.append("reason", finalReason);
      if (newDate != null) form.append("new_date", newDate);
      if (newTime != null) form.append("new_time", newTime);
      if (Array.isArray(daysAvailable)) {
        // Send as JSON string so backend can parse
        form.append("days_available", JSON.stringify(daysAvailable));
      }
      form.append("media", media);
      return requests.post(url, form);
    }

    // JSON body, use snake_case keys expected by backend via snakecaseKeys
    const body = {
      reason: finalReason,
      newDate,
      newTime,
      daysAvailable,
    };
    return requests.post(url, body, {}, true); // snakeCase = true
  },
  denyMaintenanceRequest: (id, reason) => {
    const url = `/maintenance-requests/${id}/deny/`;
    return requests.post(url, { reason });
  }
};
const Vendor = {
  vendorOnboarding: (body, token) => {
    const url = "/vendor/onboarding/";
    const headers = {
      Authorization: `Token ${token}`,
    };
    return requests.post(url, body, headers);
  },
  // Save partial onboarding data for a specific step
  vendorOnboardingPartial: (body, token) => {
    const url = "/vendor/onboarding/partial/";
    const headers = {
      Authorization: `Token ${token}`,
    };
    return requests.post(url, body, headers, false, false, true);
  },
  // Fetch latest vendor onboarding snapshot (prefill/resume)
  getVendorOnboardingMe: () => {
    const url = "/vendor/onboarding/me/";
    return requests.get(url, false);
  },
  // Fetch current vendor profile (including Stripe status)
  getVendorMe: () => {
    const url = "/users/me/";
    return requests.get(url);
  },
  // Start Stripe onboarding and receive a one-time AccountLink URL
  startStripeOnboarding: () => {
    const url = "/stripe/onboarding/start/";
    return requests.post(url, {});
  },
};
const Invoices = {
  getOpenInvoices: (page = 1, limit = 10) => {
    let url = `/invoices/open/?page=${page}&limit=${limit}`;
    return requests.get(url);
  },
  // Fetch paid invoices with pagination
  getPaidInvoices: (page = 1, limit = 10) => {
    let url = `/invoices/paid/?page=${page}&limit=${limit}`;
    return requests.get(url);
  },
  // Trigger immediate payment for an invoice (Pay Now)
  payNow: (invoiceId) => {
    if (!invoiceId) throw new Error("Missing invoice id");
    const url = `/invoices/${invoiceId}/pay-now/`;
    // No body required per backend spec
    return requests.post(url, {});
  },
  // Get a URL to view the Stripe-hosted invoice (opens in new tab)
  getInvoiceViewUrl: (invoiceId) => {
    if (!invoiceId) throw new Error("Missing invoice id");
    const url = `/invoices/${invoiceId}/view/`;
    return requests.get(url);
  },
  // Get a URL to download the Stripe invoice PDF (backend returns a direct/public URL)
  getInvoiceDownloadUrl: (invoiceId) => {
    if (!invoiceId) throw new Error("Missing invoice id");
    const url = `/invoices/${invoiceId}/download/`;
    return requests.get(url);
  },
};
// Warranty Requests API
const WarrantyRequests = {
  list: (page = 1, limit = 10) => {
    const url = `/warranty-requests/?page=${page}&limit=${limit}`;
    return requests.get(url);
  },
  getById: (id) => {
    if (!id) throw new Error("Missing warranty request id");
    return requests.get(`/warranty-requests/${id}/`);
  },
  create: ({ jobNumber, serviceCategory, property, details, media }) => {
    const url = "/warranty-requests/";

    // Build payload as JSON or multipart depending on presence of media
    if (media) {
      const form = new FormData();
      form.append("jobNumber", jobNumber);
      form.append("serviceCategory", serviceCategory);
      form.append("property", property);
      if (details) form.append("details", details);
      form.append("media", media);
      return requests.post(url, form);
    }

    // Send JSON; backend expects camelCase for input per spec
    const body = { jobNumber, serviceCategory, property };
    if (details) body.details = details;
    return requests.post(url, body, {}, false, true);
  },
  approve: (id, body = {}) => {
    if (!id) throw new Error("Missing warranty request id");
    return requests.post(`/warranty-requests/${id}/approve/`, body, {}, false, true);
  },
  deny: (id, body = {}) => {
    if (!id) throw new Error("Missing warranty request id");
    return requests.post(`/warranty-requests/${id}/deny/`, body, {}, false, true);
  },
};
const Property = {
  getProperties: (id, page = 1, limit = 10, search = "") => {
    let url = `/properties/?page=${page}&limit=${limit}`;
    if (search && search.trim()) {
      url += `&search=${encodeURIComponent(search.trim())}`;
    }
    return requests.get(url);
  },
  getPropertyById: (id) => {
    return requests.get(`/properties/${id}/`, false);
  },
  addProperties: (values) => {
    const url = "/properties/";
    return requests.post(url, values);
  },
  editProperties: (id, values) => {
    const url = `/properties/${id}/`;
    return requests.put(url, values);
  },
  deleteProperties: (id) => {
    const url = `/properties/${id}/`;
    return requests.delete(url);
  },
};
const Unit = {
  getUnits: () => {
    const url = "/units/";
    return requests.get(url);
  },
  addUnit: (values) => {
    const url = "/units/";
    return requests.post(url, values);
  },
  editUnit: (id, values) => {
    const url = `/units/${id}/`;
    return requests.put(url, values);
  },
  removeTenantFromUnit: (unitId) => {
    const url = `/units/${unitId}/remove-tenant/`;
    return requests.post(url, {});
  },
};
const PostJobApi = {
  addPostJob: (values) => {
    const url = "/maintenance-requests/";
    const snakeCase = !(values instanceof FormData);
    return requests.post(url, values, {}, snakeCase);
  },
};
const BlogApi = {
  getPosts: (page = 1) => requests.get(`/blogs/posts/?page=${page}`, true, false),
  getPostById: (id) => requests.get(`/blogs/posts/${id}/`, true, false),
  getCategories: () => requests.get("/blogs/categories/", true, false),
};
const LegalApi = {
  getDocuments: () => requests.get("/blogs/legal-documents/", true, false),
};
const Tenants = {
  tenantInvitation: (values) => {
    const url = "/tenant-invitations/bulk-create/";
    return requests.post(url, values);
  },
  tenantInvitationList: (propertyId, page = 1, limit = 10) => {
    const url = `/tenant-invitations/?property=${propertyId}&page=${page}&limit=${limit}`;
    return requests.get(url);
  },
  singleTenantInvitation: (values) => {
    const url = "/tenant-invitations/";
    return requests.post(url, values);
  },
  resendTenantInvitation: (id, values = {}) => {
    const url = `/tenant-invitations/${id}/resend/`;
    return requests.post(url, values);
  },
  removeTenantInvitation: (id) => {
    const url = `/tenant-invitations/${id}/cancel/`;
    return requests.post(url, { hard: true });
  },
  editTenantInvitation: (id, values) => {
    const url = `/tenant-invitations/${id}/`;
    return requests.put(url, values);
  },
  acceptTenantInvitation: (values) => {
    const url = "/tenant-invitations/accept/";
    return requests.post(url, values, {}, false, false, false);
  },
};

// Family Invitations API per docs
const FamilyInvitations = {
  // Create a single family invitation
  create: ({ family_member_name, family_member_email, property }) => {
    const url = "/family-invitations/";
    return requests.post(url, {
      family_member_name,
      family_member_email,
      property,
    });
  },

  // List invitations for the current homeowner; supports filters
  list: ({ property, status, page = 1, limit = 10 } = {}) => {
    let url = `/family-invitations/?page=${page}&limit=${limit}`;
    if (property) url += `&property=${property}`;
    if (status) url += `&status=${encodeURIComponent(status)}`;
    return requests.get(url);
  },

  // Retrieve single invitation
  get: (id) => requests.get(`/family-invitations/${id}/`),

  // Resend invitation (optionally with corrected email)
  resend: (id, family_member_email) => {
    const url = `/family-invitations/${id}/resend/`;
    const body = family_member_email
      ? { family_member_email }
      : {};
    return requests.post(url, body);
  },

  // Cancel invitation (must be PENDING)
  cancel: (id) => requests.post(`/family-invitations/${id}/cancel/`, {}),

  // Accept invitation (public/token-based)
  // New spec: use fi_token for family invites; support legacy token for backward compatibility
  accept: (params = {}) => {
    const { password, family_member_name } = params || {};
    const fi_token = params.fi_token || params.token; // prefer fi_token
    return requests.post(
      "/family-invitations/accept/",
      { fi_token, password, family_member_name },
      {},
      false, // snakeCase
      false, // camelCase
      false // authentication (public)
    );
  },
};

const FamilyMembers = {

}

// Service Categories API (dynamic options for vendor onboarding)
const ServiceCategories = {
  list: () => requests.get("/service-categories/"),
};

// Vendor Service Counties API (dynamic options for vendor onboarding)
const VendorServiceCounties = {
  list: () => requests.get("/vendor-service-counties/"),
};

const User = {
  register: (values) =>
    requests
      .post(
        "/auth/signup/",
        {
          ...values,
        },
        {},
        false,
        false,
        false
      )
      .then((response) => {
        const cookies = new Cookies();
        // Persist token from either access or token field
        const authToken = response.data?.access || response.data?.token;
        if (authToken)
          cookies.set("token", authToken, {
            path: "/",
            sameSite: "lax",
            secure: window.location.protocol === "https:",
          });
        return response;
      }),
  login: (email, password) =>
    requests
      .post(
        "/users/authenticate/",
        {
          username: email,
          password: password,
        },
        {},
        false,
        false,
        false
      )
      .then((response) => {
        const cookies = new Cookies();
        // Persist token from either access or token field
        const authToken = response.data?.access || response.data?.token;
        if (authToken)
          cookies.set("token", authToken, {
            path: "/",
            sameSite: "lax",
            secure: window.location.protocol === "https:",
          });
        return response;
      }),
  requestPasswordReset: (values) =>
    requests.post(
      "/users/me/request-password-reset/",
      {
        email: values.email,
      },
      {},
      false,
      false,
      false
    ),
  confirmPasswordReset: (values) =>
    requests.post(
      "/users/me/confirm-password/",
      values,
      {},
      false,
      false,
      false
    ),
  changePassword: (newPassword) =>
    requests.post("/users/me/authenticated-change-password/", {
      password: newPassword,
    }),
  getMe: () => requests.get("/users/me/"),

  verifyPasswordResetToken: (passwordResetToken) =>
    requests.post("/users/me/verify-password-token/", {
      token: passwordResetToken,
    }),

  authenticatedPasswordChange: (currentPassword, newPassword) =>
    requests.post("/users/me/authenticated-change-password/", {
      currentPassword,
      newPassword,
    }),

  logout: (history) => {
    const cookies = new Cookies();
    cookies.remove("token");
    cookies.remove("user");
    delete axios.defaults.headers.common["Authorization"];
    history.replace("/login");
  },
  getStatistics: () => {
    return requests.get("/users/statistics/");
  },
  updateMe: (values) => requests.patch("/users/me/", values),
  markPaymentMethod: () => requests.post("/invoices/mark-payment-method/", {}),

  // Save a Stripe payment method on backend.
  saveStripePaymentMethod: (paymentMethodId) =>
    requests.post(
      "/stripe/payment-methods/save/",
      { payment_method_id: paymentMethodId }
    ),

  // Subscribe to newsletter
  subscribeNewsletter: (email) =>
    requests.post(
      "/newsletter/",
      { email },
      {},
      false, // snakeCase
      false, // camelCase
      false // authentication
    ),
};

const LandloardUser = {
  sendTenantInvite: (values) => {
    return requests.post("/tenant-invitations/", values);
  },
  resendTenantInvite: (id) => {
    return requests.post(`/tenant-invitations/${id}/resend/`, {});
  },
};

const Messages = {
  // Get total unread count across all chats
  getUnreadTotal: () => {
    const url = `/chat/threads/unread_total/`;
    return requests.get(url);
  },

  // Get unread count for specific thread
  getUnreadCount: (threadId) => {
    const url = `/chat/threads/${threadId}/unread_count/`;
    return requests.get(url);
  },

  // Get messages in a thread
  getThreadMessages: (threadId, page = 1, limit = 50) => {
    const url = `/chat/threads/${threadId}/messages/?page=${page}&limit=${limit}`;
    return requests.get(url);
  },

  // Mark all messages in thread as read
  markAllAsRead: (threadId) => {
    const url = `/chat/threads/${threadId}/mark_all_read/`;
    return requests.post(url, {});
  },

  // Send a new message
  sendMessage: (message, threadId = null) => {
    const url = `/chat/messages/`;
    const body = { message };
    if (threadId) {
      body.thread_id = threadId;
    }
    return requests.post(url, body);
  },

  // Legacy methods for backward compatibility
  getMessagesList: (page = 1, limit = 10, search = "") => {
    // This might need to be updated based on your actual threads API
    let url = `/chat/threads/?page=${page}&limit=${limit}`;
    if (search && search.trim()) {
      url += `&search=${encodeURIComponent(search.trim())}`;
    }
    return requests.get(url, false);
  },

  getMessageThread: (threadId, page = 1, limit = 50) => {
    return this.getThreadMessages(threadId, page, limit);
  },

  markAsRead: (threadId, messageId = null) => {
    return this.markAllAsRead(threadId);
  },
};

export const LeadMagnetApi = {
  // GET /api/leads/page/ — fetch active landing page content (no auth required)
  getPage: () =>
    requests.get("/leads/page/", true, false).then((response) => response.data),

  // GET /api/leads/page/<slug>/ — fetch a specific campaign page (no auth required)
  getCampaignPage: (slug) =>
    requests.get(`/leads/page/${slug}/`, true, false).then((response) => response.data),

  // POST /api/leads/submit/ — submit lead capture form (no auth required)
  submit: (values) => requests.post("/leads/submit/", values, {}, true, true, false),

  // POST /api/leads/submit/<slug>/ — submit a specific campaign form (no auth required)
  submitCampaign: (slug, values) =>
    requests.post(`/leads/submit/${slug}/`, values, {}, true, true, false),

  // GET /api/leads/ebook-url/ — fetch the eBook download URL (no auth required)
  getEbookUrl: () => requests.get("/leads/ebook-url/", true, false),
};

export {
  User,
  Property,
  LandloardUser,
  JobsApi,
  Unit,
  Tenants,
  PostJobApi,
  BlogApi,
  LegalApi,
  Vendor,
  Invoices,
  WarrantyRequests,
  Messages,
  FamilyInvitations,
  ServiceCategories,
  VendorServiceCounties,
};
